const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '19 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, scholarly, and ecclesial sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

const source = (id, author, title, detail, href) => ({ id, author, title, detail, href });

export const eschatologyPerspectives = [
    {
        slug: 'is-hell-eternal',
        category: 'Death and Final Things',
        question: 'Is hell eternal?',
        shortTitle: 'Is hell eternal?',
        summary: 'Christians agree that Scripture warns of final judgement and that evil will not endure within God’s completed kingdom. They disagree over whether hell is everlasting conscious punishment, a judgement that ends in the destruction of the wicked, or a severe but ultimately restorative judgement through which God finally reconciles every person.',
        introduction: 'The disagreement turns on how descriptions of fire, death, destruction, exclusion, punishment, and “the age to come” should be read together. It also involves larger convictions about human freedom, divine justice, immortality, the purpose of judgement, and the scope of Christ’s victory. The readings below concern final destiny; the separate question “What happens after death?” distinguishes this from the intermediate state before resurrection and final judgement.',
        seoDescription: 'Compare Christian views of eternal hell: eternal conscious punishment, annihilation or conditional immortality, and universal reconciliation.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Matthew 25:31–46', href: '/read/Matt.25/#Matt.25.31-Matt.25.46' },
            { label: 'Mark 9:42–50', href: '/read/Mark.9/#Mark.9.42-Mark.9.50' },
            { label: '2 Thessalonians 1:5–10', href: '/read/2Thess.1/#2Thess.1.5-2Thess.1.10' },
            { label: 'Revelation 20:10–15', href: '/read/Rev.20/#Rev.20.10-Rev.20.15' },
        ],
        readings: [
            {
                id: 'eternal-conscious-punishment',
                title: 'The lost experience everlasting conscious punishment',
                thesis: 'Those finally opposed to God remain conscious under an irreversible judgement whose duration has no end.',
                accent: 'navy',
                badges: ['Conservative', 'Traditional Protestant', 'Reformed', 'Evangelical'],
                summary: 'This reading takes the parallel between “eternal punishment” and “eternal life” in Matthew 25, together with Revelation’s images of unending torment, as evidence that final punishment continues without end. Hell is not treated as a rival kingdom where evil survives independently, but as the settled judgement of God upon creatures who remain unreconciled to him.',
                contribution: 'It gives full weight to the strongest biblical warnings, the gravity of rejecting God, and the finality of judgement. It also resists reducing judgement to a temporary educational process when several texts present it as decisive and irreversible.',
                caution: 'This view must explain how everlasting suffering accords with God’s goodness and final victory, how highly symbolic apocalyptic images should control doctrine, and whether biblical language of death and destruction naturally implies continuing conscious existence.',
                supporters: [
                    { name: 'Westminster Confession of Faith', sourceId: 'westminster-final-judgement' },
                    { name: 'Denny Burk', sourceId: 'four-views-hell' },
                ],
                predicates: [
                    {
                        label: 'Final punishment is conscious, irreversible, and without end',
                        topicSlug: 'what-is-eternal-conscious-torment',
                        readingId: 'retributive-eternal-punishment',
                        linked: true,
                    },
                    {
                        label: 'Personal consciousness continues between death and bodily resurrection',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'conscious-intermediate-state',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'terminal-destruction', linked: true },
                    { type: 'contrasts with', target: 'universal-restoration', linked: true },
                    {
                        type: 'raises',
                        label: 'What does eternal conscious torment mean?',
                        topicSlug: 'what-is-eternal-conscious-torment',
                        linked: true,
                    },
                ],
            },
            {
                id: 'terminal-destruction',
                title: 'The lost are finally destroyed',
                thesis: 'Final punishment is irreversible and eternal in effect, but the wicked ultimately perish rather than remain conscious forever.',
                accent: 'amber',
                badges: ['Conditionalist', 'Adventist', 'Evangelical', 'Moderate'],
                summary: 'Conditionalists argue that immortality is God’s gift in Christ rather than an indestructible possession of every human soul. Judgement is real and may include conscious suffering proportionate to guilt, but its final result is the second death: the complete destruction of those who refuse life in God. “Eternal punishment” therefore describes a punishment with an everlasting result rather than an endlessly continuing process.',
                contribution: 'It reads death, perish, consume, and destroy in their ordinary force while preserving the seriousness and finality of judgement. It also presents eternal life as a gift given to the redeemed rather than an unavoidable condition shared by the saved and lost alike.',
                caution: 'This view must account for passages that portray unending fire, smoke, contempt, or torment and show why “eternal punishment” should describe permanent effect when “eternal life” describes continuing experience.',
                supporters: [
                    { name: 'John G. Stackhouse Jr.', sourceId: 'four-views-hell' },
                    { name: 'Seventh-day Adventist Church', sourceId: 'adventist-end-of-sin' },
                ],
                predicates: [
                    {
                        label: 'Immortality is conditional upon God’s gift of life',
                        topicSlug: 'does-final-punishment-end-in-annihilation',
                        readingId: 'conditional-immortality',
                        linked: true,
                    },
                    {
                        label: 'The final Christian hope centres on resurrection rather than a naturally immortal soul',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'resurrection-and-new-creation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'eternal-conscious-punishment', linked: true },
                    { type: 'contrasts with', target: 'universal-restoration', linked: true },
                    {
                        type: 'raises',
                        label: 'Does final punishment end in annihilation?',
                        topicSlug: 'does-final-punishment-end-in-annihilation',
                        linked: true,
                    },
                ],
            },
            {
                id: 'universal-restoration',
                title: 'God finally reconciles everyone',
                thesis: 'Hell is a real expression of divine judgement, but it does not finally defeat God’s purpose to reconcile every person through Christ.',
                accent: 'teal',
                badges: ['Universalist', 'Progressive', 'Patristic', 'Ecumenical'],
                summary: 'Christian universalists read judgement as severe, truthful, and purifying rather than endlessly retributive. They appeal to texts in which God reconciles all things, becomes “all in all”, and subjects every creature to Christ. On this view, divine judgement exposes and consumes evil so that creation is healed; it does not preserve an everlasting population of the lost.',
                contribution: 'It foregrounds the universal scope of God’s saving purpose, the cosmic reach of Christ’s victory, and the conviction that divine justice restores what sin has damaged. It also refuses to treat universal salvation as automatic indulgence by retaining judgement, repentance, and transformation.',
                caution: 'This view must explain texts that describe exclusion or punishment as final, avoid turning warnings into empty rhetoric, and show how eventual reconciliation honours rather than overrides creaturely freedom and the reality of unrepented evil.',
                supporters: [
                    { name: 'David Bentley Hart', sourceId: 'hart-all-saved' },
                    { name: 'Robin Parry', sourceId: 'four-views-hell' },
                ],
                predicates: [
                    {
                        label: 'God’s judgement ultimately serves the reconciliation of every person',
                        topicSlug: 'what-is-christian-universalism',
                        readingId: 'confident-universal-reconciliation',
                        linked: true,
                    },
                    {
                        label: 'Resurrection and renewed creation define the final horizon of salvation',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'resurrection-and-new-creation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'eternal-conscious-punishment', linked: true },
                    { type: 'contrasts with', target: 'terminal-destruction', linked: true },
                    {
                        type: 'raises',
                        label: 'What is Christian universalism?',
                        topicSlug: 'what-is-christian-universalism',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'stanford-heaven-hell',
                'Stanford Encyclopedia of Philosophy',
                'Heaven and Hell in Christian Thought',
                'An academic overview of the main Christian accounts of final destiny and their philosophical questions about freedom, justice, punishment, and universal reconciliation.',
                'https://plato.stanford.edu/entries/heaven-hell/',
            ),
            source(
                'four-views-hell',
                'Preston Sprinkle, editor',
                'Four Views on Hell, Second Edition',
                'An academic publisher’s presentation of Denny Burk on eternal conscious torment, John G. Stackhouse Jr. on conditional immortality, Robin Parry on universal reconciliation, and Jerry Walls on purgatory.',
                'https://zondervanacademic.com/products/four-views-on-hell1',
            ),
            source(
                'westminster-final-judgement',
                'Orthodox Presbyterian Church',
                'Westminster Confession of Faith, Chapters 32–33',
                'A Reformed confessional account of the intermediate state, resurrection, final judgement, everlasting life, and eternal punishment.',
                'https://www.opc.org/documents/MESV_col.html',
            ),
            source(
                'adventist-end-of-sin',
                'Seventh-day Adventist Church',
                'The Millennium and the End of Sin',
                'An official conditionalist account in which the wicked are raised for judgement and finally destroyed so that sin and death cease.',
                'https://www.adventist.org/millennium-and-the-end-of-sin/the-millenium-and-the-end-of-sin/',
            ),
            source(
                'hart-all-saved',
                'David Bentley Hart',
                'That All Shall Be Saved: Heaven, Hell, and Universal Salvation',
                'A contemporary theological argument that God’s goodness, freedom, and purpose as creator entail the eventual reconciliation of all persons.',
                'https://yalebooks.yale.edu/book/9780300258486/that-all-shall-be-saved/',
            ),
        ],
        generation,
    },
    {
        slug: 'what-is-eternal-conscious-torment',
        category: 'Death and Final Things',
        question: 'What does eternal conscious torment mean?',
        shortTitle: 'Eternal conscious torment explained',
        summary: 'Eternal conscious torment is the view that final punishment continues without end and that those under judgement remain conscious. Its supporters nevertheless explain the character of that punishment differently: as continuing retributive judgement, definitive self-exclusion from communion with God, or the painful experience of God’s unveiled presence by a will that remains opposed to divine love.',
        introduction: 'The label can imply a single detailed theory, but historic traditions use different biblical images and metaphysical accounts. Some emphasise punishment imposed by a just judge, some the settled consequence of refusing God, and some that heaven and hell are different experiences of the same divine presence. These variants overlap and each still faces the moral and exegetical questions raised by everlasting suffering.',
        seoDescription: 'Understand eternal conscious torment through retributive, self-exclusion, and divine-presence accounts of everlasting hell.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Matthew 25:41–46', href: '/read/Matt.25/#Matt.25.41-Matt.25.46' },
            { label: 'Luke 16:19–31', href: '/read/Luke.16/#Luke.16.19-Luke.16.31' },
            { label: '2 Thessalonians 1:5–10', href: '/read/2Thess.1/#2Thess.1.5-2Thess.1.10' },
            { label: 'Revelation 14:9–12', href: '/read/Rev.14/#Rev.14.9-Rev.14.12' },
        ],
        readings: [
            {
                id: 'retributive-eternal-punishment',
                title: 'Hell is continuing punishment for sin',
                thesis: 'God justly and consciously punishes final, unrepented rebellion for ever, and the punishment never reaches a terminal point.',
                accent: 'navy',
                badges: ['Conservative', 'Reformed', 'Traditional Protestant', 'Evangelical'],
                summary: 'This account treats hell first as a judicial sentence. The risen wicked remain under God’s righteous judgement, described through exclusion, destruction, and torment, without being restored or ceasing to exist. The duration is read as parallel to eternal life, while differing degrees of punishment may correspond to differing knowledge and guilt.',
                contribution: 'It preserves the judicial force and finality of biblical warnings and stresses that evil cannot be dismissed as finite inconvenience. It also makes judgement an act of God’s moral governance rather than merely the impersonal consequence of human choice.',
                caution: 'The view must explain why just punishment requires endless conscious suffering, whether finite creatures can incur an infinite penalty, and how such a state belongs within a creation in which God has finally defeated evil.',
                supporters: [
                    { name: 'Westminster Confession of Faith', sourceId: 'westminster-hell' },
                    { name: 'Denny Burk', sourceId: 'four-views-ect' },
                ],
                predicates: [
                    {
                        label: 'The eternal duration of punishment parallels the eternal duration of life',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'eternal-conscious-punishment',
                        linked: true,
                    },
                    {
                        label: 'The departed remain conscious while awaiting resurrection and final judgement',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'conscious-intermediate-state',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'overlaps with', target: 'definitive-self-exclusion', linked: true },
                    { type: 'contrasts with', target: 'unveiled-divine-presence', linked: true },
                    {
                        type: 'raises',
                        label: 'Is hell eternal?',
                        topicSlug: 'is-hell-eternal',
                        linked: true,
                    },
                ],
            },
            {
                id: 'definitive-self-exclusion',
                title: 'Hell is a final refusal of communion with God',
                thesis: 'Hell is the everlasting condition of a person who freely and finally refuses communion with God and neighbour.',
                accent: 'amber',
                badges: ['Catholic', 'Traditional', 'Moderate', 'Pastoral'],
                summary: 'This account describes hell relationally. God desires salvation and predestines no one to damnation, yet love does not coerce communion. Final loss is therefore both judgement and the settled result of refusing grace: a person remains separated from the life for which they were created. The language of fire expresses the terrible reality of that irreversible exclusion rather than requiring a material theory of flames.',
                contribution: 'It holds divine judgement together with creaturely responsibility and explicitly rejects the picture of a God who delights in suffering. It also shows that sin is not merely rule-breaking but a refusal of communion, love, and the source of life.',
                caution: 'The view must explain how a finite and damaged will becomes irrevocably fixed, whether perfect knowledge of God could coexist with endless refusal, and how “self-exclusion” relates to texts in which God actively judges and sentences.',
                supporters: [
                    { name: 'Catechism of the Catholic Church', sourceId: 'catechism-hell' },
                ],
                predicates: [
                    {
                        label: 'Hell remains an everlasting and conscious final loss',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'eternal-conscious-punishment',
                        linked: true,
                    },
                    {
                        label: 'Universal reconciliation is not certain because final refusal remains possible',
                        topicSlug: 'what-is-christian-universalism',
                        readingId: 'final-loss-remains-possible',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'retributive-eternal-punishment', linked: true },
                    { type: 'overlaps with', target: 'unveiled-divine-presence', linked: true },
                    {
                        type: 'raises',
                        label: 'What is Christian universalism?',
                        topicSlug: 'what-is-christian-universalism',
                        linked: true,
                    },
                ],
            },
            {
                id: 'unveiled-divine-presence',
                title: 'God’s presence becomes torment for the resistant',
                thesis: 'Heaven and hell are contrasting experiences of the same unveiled divine presence: joy to those who love God and torment to those who remain opposed to that love.',
                accent: 'red',
                badges: ['Orthodox', 'Eastern Christian', 'Traditional', 'Mystical'],
                summary: 'This Eastern Christian account resists imagining hell as a region from which God is absent, because no creature exists outside God’s sustaining presence. At resurrection and judgement, divine glory is fully manifest. The person healed by love experiences that glory as paradise; the person who hates love experiences the same reality as burning anguish and death.',
                contribution: 'It unifies final judgement with the Christian confession that God is everywhere present and that divine love does not change into hatred. It also makes present spiritual formation significant: heaven and hell disclose what a person has become in relation to God.',
                caution: 'The account must clarify whether opposition can remain for ever, how its experiential language relates to Scripture’s judicial images, and how far this formulation represents the range of Orthodox teaching rather than one explanatory emphasis within it.',
                supporters: [
                    { name: 'Orthodox Church in America', sourceId: 'oca-heaven-hell' },
                ],
                predicates: [
                    {
                        label: 'Final loss is a conscious and everlasting encounter with divine judgement',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'eternal-conscious-punishment',
                        linked: true,
                    },
                    {
                        label: 'The final state follows bodily resurrection into God’s renewed creation',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'resurrection-and-new-creation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'retributive-eternal-punishment', linked: true },
                    { type: 'overlaps with', target: 'definitive-self-exclusion', linked: true },
                    {
                        type: 'raises',
                        label: 'What happens after death?',
                        topicSlug: 'what-happens-after-death',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'westminster-hell',
                'Orthodox Presbyterian Church',
                'Westminster Confession of Faith, Chapters 32–33',
                'A Reformed confessional account of conscious existence after death, bodily resurrection, final judgement, and eternal punishment.',
                'https://www.opc.org/documents/MESV_col.html',
            ),
            source(
                'four-views-ect',
                'Preston Sprinkle, editor',
                'Four Views on Hell, Second Edition',
                'An academic publisher’s description of Denny Burk’s defence of eternal conscious torment alongside conditionalist, universalist, and purgatorial alternatives.',
                'https://zondervanacademic.com/products/four-views-on-hell1',
            ),
            source(
                'catechism-hell',
                'Catechism of the Catholic Church',
                'Article 12, IV. Hell',
                'An official Catholic account of hell as definitive self-exclusion from communion with God, eternal separation, and a judgement God does not predestine anyone to receive.',
                'https://www.vatican.va/content/catechism/en/part_one/section_two/chapter_three/article_12/iv_hell.html',
            ),
            source(
                'oca-heaven-hell',
                'Orthodox Church in America',
                'Heaven and Hell',
                'An Orthodox account in which the same divine presence is paradise to those who love God and torment to those who remain opposed to divine love.',
                'https://www.oca.org/orthodoxy/the-orthodox-faith/spirituality/the-kingdom-of-heaven/heaven-and-hell',
            ),
            source(
                'stanford-ect',
                'Stanford Encyclopedia of Philosophy',
                'Heaven and Hell in Christian Thought',
                'An academic analysis of traditional hell, freedom-based defences, retributive punishment, and major alternatives.',
                'https://plato.stanford.edu/entries/heaven-hell/',
            ),
        ],
        generation,
    },
    {
        slug: 'does-final-punishment-end-in-annihilation',
        category: 'Death and Final Things',
        question: 'Does final punishment end in annihilation?',
        shortTitle: 'Annihilationism and conditional immortality',
        summary: 'Annihilationism holds that the final result of judgement is the destruction of the wicked rather than their everlasting conscious suffering. Advocates usually ground this in conditional immortality, but differ over the experience preceding destruction. Critics answer that biblical language of death and destruction can describe ruin, exclusion, or loss while the person continues to exist.',
        introduction: 'The debate is not between taking judgement seriously and dismissing it. Conditionalists affirm resurrection, judgement, punishment, and an irreversible second death. The disputed questions are whether every human is inherently immortal, whether eternal punishment names an everlasting process or result, and how images of destruction should be related to texts about continuing shame, smoke, or torment.',
        seoDescription: 'Compare conditional immortality, terminal punishment, and the traditional critique that biblical destruction does not mean extinction.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Matthew 10:26–33', href: '/read/Matt.10/#Matt.10.26-Matt.10.33' },
            { label: 'John 3:14–21', href: '/read/John.3/#John.3.14-John.3.21' },
            { label: 'Romans 6:20–23', href: '/read/Rom.6/#Rom.6.20-Rom.6.23' },
            { label: 'Revelation 20:11–15', href: '/read/Rev.20/#Rev.20.11-Rev.20.15' },
        ],
        readings: [
            {
                id: 'conditional-immortality',
                title: 'Immortality is given only in Christ',
                thesis: 'Human beings are not naturally indestructible; God grants everlasting life to the redeemed while the finally impenitent perish in the second death.',
                accent: 'teal',
                badges: ['Conditionalist', 'Adventist', 'Evangelical', 'Biblical Theology'],
                summary: 'This reading begins with the repeated biblical contrast between life and death. God alone possesses immortality inherently, while immortality is bestowed upon the redeemed at resurrection. The wicked are raised and judged but are finally consumed, so the everlasting result of punishment is non-life rather than an everlasting form of conscious life in misery.',
                contribution: 'It connects the doctrine of judgement to the Bible’s central promise of life in Christ and gives ordinary force to words such as die, perish, consume, and destroy. It also explains how evil can be decisively removed from the renewed creation.',
                caution: 'The view must show that destruction language refers to cessation of conscious existence rather than devastation or exclusion and must account for passages in which punishment or contempt appears to continue without end.',
                supporters: [
                    { name: 'Seventh-day Adventist Church', sourceId: 'adventist-millennium' },
                    { name: 'John G. Stackhouse Jr.', sourceId: 'four-views-annihilation' },
                ],
                predicates: [
                    {
                        label: 'The eternal consequence of judgement is final destruction rather than endless suffering',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'terminal-destruction',
                        linked: true,
                    },
                    {
                        label: 'Death is an unconscious sleep until bodily resurrection',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'unconscious-sleep-until-resurrection',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'develops into', target: 'terminal-punishment', linked: true },
                    { type: 'contrasts with', target: 'ruin-not-extinction', linked: true },
                    {
                        type: 'raises',
                        label: 'Is hell eternal?',
                        topicSlug: 'is-hell-eternal',
                        linked: true,
                    },
                ],
            },
            {
                id: 'terminal-punishment',
                title: 'Punishment ends in destruction',
                thesis: 'The wicked may consciously experience judgement proportionate to their guilt before God brings their existence to a final and irreversible end.',
                accent: 'amber',
                badges: ['Conditionalist', 'Evangelical', 'Moderate', 'Retributive'],
                summary: 'This variant separates annihilationism from the claim that every condemned person is destroyed immediately. Biblical statements about degrees of judgement can be honoured through a conscious penal process, while death and destruction still name its terminal outcome. The judgement is eternal because it cannot be reversed, not because every act of punishment continues for ever.',
                contribution: 'It combines the conditionalist reading of final death with biblical language about measured responsibility and punishment. This prevents annihilation from becoming an identical sentence experienced in precisely the same way regardless of knowledge, harm, or guilt.',
                caution: 'The model must avoid specifying a postmortem timetable Scripture does not provide and must explain why proportionate punishment ends in extinction rather than restoration or continuing exclusion.',
                supporters: [
                    { name: 'John G. Stackhouse Jr.', sourceId: 'four-views-annihilation' },
                ],
                predicates: [
                    {
                        label: 'Final destruction is an irreversible punishment with everlasting effect',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'terminal-destruction',
                        linked: true,
                    },
                    {
                        label: 'Conscious existence after death can precede resurrection and final sentence',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'conscious-intermediate-state',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'depends on', target: 'conditional-immortality', linked: true },
                    { type: 'contrasts with', target: 'ruin-not-extinction', linked: true },
                ],
            },
            {
                id: 'ruin-not-extinction',
                title: 'Destruction means ruin rather than extinction',
                thesis: 'Biblical death and destruction language describes irreversible loss, exclusion, and ruin without requiring the person to cease conscious existence.',
                accent: 'navy',
                badges: ['Conservative', 'Traditional Protestant', 'Reformed', 'Critical Response'],
                summary: 'Traditional interpreters argue that words such as “destroy” do not always mean reduction to non-existence and must be read beside passages describing continuing punishment. On this account, the second death is a dreadful condition of estrangement under judgement, not the end of personal existence. The imagery is cumulative: death, exclusion, and torment together describe the loss of blessed life with God.',
                contribution: 'It cautions against building a doctrine from the presumed meaning of a single English word and insists that the full canonical range of judgement texts be held together. It also preserves the symmetry many see between everlasting life and everlasting punishment.',
                caution: 'The view must explain why the dominant biblical contrast between life and death should mean two modes of everlasting conscious existence and whether “ruin” adequately captures promises that evil and death themselves will be abolished.',
                supporters: [
                    { name: 'Denny Burk', sourceId: 'four-views-annihilation' },
                    { name: 'Westminster Confession of Faith', sourceId: 'westminster-annihilation' },
                ],
                predicates: [
                    {
                        label: 'Final punishment remains conscious and without end',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'eternal-conscious-punishment',
                        linked: true,
                    },
                    {
                        label: 'Judgement continues as a retributive sentence rather than terminating existence',
                        topicSlug: 'what-is-eternal-conscious-torment',
                        readingId: 'retributive-eternal-punishment',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'conditional-immortality', linked: true },
                    { type: 'contrasts with', target: 'terminal-punishment', linked: true },
                    {
                        type: 'raises',
                        label: 'What does eternal conscious torment mean?',
                        topicSlug: 'what-is-eternal-conscious-torment',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'four-views-annihilation',
                'Preston Sprinkle, editor',
                'Four Views on Hell, Second Edition',
                'An academic publisher’s presentation of John G. Stackhouse Jr.’s conditionalist account and Denny Burk’s eternal-conscious-punishment response.',
                'https://zondervanacademic.com/products/four-views-on-hell1',
            ),
            source(
                'adventist-millennium',
                'Seventh-day Adventist Church',
                'The Millennium and the End of Sin',
                'An official Adventist statement that the wicked are raised for judgement and finally destroyed, bringing sin and death to an end.',
                'https://www.adventist.org/millennium-and-the-end-of-sin/the-millenium-and-the-end-of-sin/',
            ),
            source(
                'westminster-annihilation',
                'Orthodox Presbyterian Church',
                'Westminster Confession of Faith, Chapters 32–33',
                'A Reformed confessional source affirming conscious existence after death and everlasting punishment rather than final extinction.',
                'https://www.opc.org/documents/MESV_col.html',
            ),
            source(
                'stanford-annihilation',
                'Stanford Encyclopedia of Philosophy',
                'Heaven and Hell in Christian Thought',
                'An academic overview of traditional hell, annihilation, universalism, and the arguments each view must address.',
                'https://plato.stanford.edu/entries/heaven-hell/',
            ),
            source(
                'cambridge-annihilation-universalism',
                'Kirk MacGregor',
                'Annihilation or salvation? A philosophical case for preferring universalism to annihilationism',
                'A peer-reviewed philosophical critique of annihilationism from a universalist perspective, useful for testing the view’s account of divine goodness and victory.',
                'https://www.cambridge.org/core/journals/religious-studies/article/abs/annihilation-or-salvation-a-philosophical-case-for-preferring-universalism-to-annihilationism/C361D06C582BFCE33984D8DB33D9BBE2',
            ),
        ],
        generation,
    },
    {
        slug: 'what-is-christian-universalism',
        category: 'Death and Final Things',
        question: 'What is Christian universalism, and will God ultimately reconcile everyone?',
        shortTitle: 'Christian universalism explained',
        summary: 'Christian universalism is the conviction that God will ultimately reconcile every person through Christ. Some present this as the necessary conclusion of the gospel, while hopeful universalists refuse to declare the outcome but believe Christians may reasonably pray and hope for all. Traditional critics maintain that Scripture leaves final and irreversible loss as a real possibility.',
        introduction: 'Christian universalism should not be confused with the claim that every religion is identical, that judgement is unreal, or that repentance is unnecessary. Its distinctive claim concerns the eventual outcome of Christ’s saving work. The central disagreements are whether judgement is restorative or final, whether creaturely freedom can resist God for ever, and how universal-sounding promises should be related to warnings of exclusion and destruction.',
        seoDescription: 'Compare confident Christian universalism, hopeful universalism, and the traditional view that final loss remains possible.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Romans 5:12–21', href: '/read/Rom.5/#Rom.5.12-Rom.5.21' },
            { label: '1 Corinthians 15:20–28', href: '/read/1Cor.15/#1Cor.15.20-1Cor.15.28' },
            { label: 'Philippians 2:5–11', href: '/read/Phil.2/#Phil.2.5-Phil.2.11' },
            { label: 'Colossians 1:15–20', href: '/read/Col.1/#Col.1.15-Col.1.20' },
        ],
        readings: [
            {
                id: 'confident-universal-reconciliation',
                title: 'Everyone will finally be reconciled to God',
                thesis: 'God’s purpose in Christ will finally overcome every creature’s resistance, so judgement ends in the free reconciliation and restoration of all persons.',
                accent: 'teal',
                badges: ['Universalist', 'Progressive', 'Patristic', 'Ecumenical'],
                summary: 'This reading treats universal salvation as a theological conclusion rather than a permitted wish. God creates every person for communion, Christ assumes and heals humanity, and the Spirit brings God’s purpose to completion. Judgement can be severe and prolonged, but it exposes falsehood, corrects the will, and destroys sin rather than preserving endless alienation.',
                contribution: 'It gathers the Bible’s universal promises into a coherent account of creation and redemption and asks whether an everlasting realm of loss would leave God’s saving purpose permanently frustrated. It also insists that salvation restores freedom instead of competing with it.',
                caution: 'The view must account for texts that speak of irreversible exclusion, avoid weakening the urgency of repentance, and demonstrate rather than merely assume that every divine punishment is remedial and every will is finally healable.',
                supporters: [
                    { name: 'David Bentley Hart', sourceId: 'hart-universal' },
                    { name: 'Robin Parry', sourceId: 'four-views-universal' },
                    { name: 'Ilaria Ramelli', sourceId: 'ramelli-apokatastasis' },
                ],
                predicates: [
                    {
                        label: 'Hell is real but ultimately serves universal restoration',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'universal-restoration',
                        linked: true,
                    },
                    {
                        label: 'The final horizon is the resurrection and renewal of creation',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'resurrection-and-new-creation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'overlaps with', target: 'hopeful-universalism', linked: true },
                    { type: 'contrasts with', target: 'final-loss-remains-possible', linked: true },
                    {
                        type: 'raises',
                        label: 'Is hell eternal?',
                        topicSlug: 'is-hell-eternal',
                        linked: true,
                    },
                ],
            },
            {
                id: 'hopeful-universalism',
                title: 'We may hope for everyone without certainty',
                thesis: 'Christians may pray and hope that every person will be saved while refusing to claim knowledge that Scripture and the final judgement have not disclosed.',
                accent: 'amber',
                badges: ['Hopeful Universalism', 'Moderate', 'Catholic', 'Reformed'],
                summary: 'This position distinguishes confident expectation from dogmatic assertion. God’s universal saving will and Christ’s victory give substantial reason for hope, yet warnings of judgement remain real and human response cannot be treated as predetermined. The church therefore witnesses, prays, and entrusts the final outcome to God rather than announcing either universal salvation or the damnation of particular persons.',
                contribution: 'It preserves the breadth of Christian hope without claiming more certainty than its advocates believe revelation permits. It can also sustain evangelistic urgency while resisting speculation about who is lost.',
                caution: 'Critics ask whether this is a stable interpretation or only a posture of agnosticism, whether its hope has sufficient scriptural warrant, and whether declining to state an expected outcome avoids rather than resolves the underlying theological tensions.',
                supporters: [
                    { name: 'Thomas F. Torrance, as interpreted in recent scholarship', sourceId: 'torrance-universalism' },
                ],
                predicates: [
                    {
                        label: 'Universal reconciliation is a genuine Christian hope even when it is not asserted as certain',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'universal-restoration',
                        linked: true,
                    },
                    {
                        label: 'Final loss remains a serious possibility rather than an outcome Christians assign to individuals',
                        topicSlug: 'what-is-eternal-conscious-torment',
                        readingId: 'definitive-self-exclusion',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'confident-universal-reconciliation', linked: true },
                    { type: 'overlaps with', target: 'final-loss-remains-possible', linked: true },
                ],
            },
            {
                id: 'final-loss-remains-possible',
                title: 'Final loss remains possible and irreversible',
                thesis: 'God sincerely wills salvation and Christ is sufficient for all, but Scripture does not promise that every person will finally receive that salvation.',
                accent: 'navy',
                badges: ['Conservative', 'Catholic', 'Reformed', 'Evangelical'],
                summary: 'Traditional critics of universalism affirm God’s universal invitation while reading warnings of exclusion, destruction, and punishment as descriptions of a genuinely possible final outcome. Love permits a response that is not coerced, and the judgement passages do not clearly promise postmortem repentance or a universally remedial process. Christians may hope widely but should not turn hope into assurance where the texts continue to warn.',
                contribution: 'It protects the seriousness of biblical warnings, moral responsibility, and the urgency of reconciliation with God. It also distinguishes confidence in Christ’s sufficiency from a claim that every person will inevitably participate in its saving result.',
                caution: 'The view must relate everlasting loss to God’s universal saving will and cosmic victory and explain whether created freedom can eternally frustrate the purpose for which God created and sustains a person.',
                supporters: [
                    { name: 'Catechism of the Catholic Church', sourceId: 'catechism-universal' },
                    { name: 'Westminster Confession of Faith', sourceId: 'westminster-universal' },
                ],
                predicates: [
                    {
                        label: 'Some persons may remain under an eternal and conscious final judgement',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'eternal-conscious-punishment',
                        linked: true,
                    },
                    {
                        label: 'Final loss can be understood as a definitive refusal of communion',
                        topicSlug: 'what-is-eternal-conscious-torment',
                        readingId: 'definitive-self-exclusion',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'confident-universal-reconciliation', linked: true },
                    { type: 'qualifies', target: 'hopeful-universalism', linked: true },
                    {
                        type: 'raises',
                        label: 'What happens after death?',
                        topicSlug: 'what-happens-after-death',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'hart-universal',
                'David Bentley Hart',
                'That All Shall Be Saved: Heaven, Hell, and Universal Salvation',
                'A contemporary theological argument that the goodness and freedom of God as creator entail the eventual salvation of every person.',
                'https://yalebooks.yale.edu/book/9780300258486/that-all-shall-be-saved/',
            ),
            source(
                'four-views-universal',
                'Preston Sprinkle, editor',
                'Four Views on Hell, Second Edition',
                'An academic publisher’s presentation of Robin Parry’s case for universal reconciliation alongside three major alternatives.',
                'https://zondervanacademic.com/products/four-views-on-hell1',
            ),
            source(
                'ramelli-apokatastasis',
                'Ilaria L. E. Ramelli',
                'The Christian Doctrine of Apokatastasis',
                'A major historical study of early Christian arguments for the eventual restoration of rational creatures and the defeat of evil.',
                'https://brill.com/abstract/title/16787?language=en',
            ),
            source(
                'torrance-universalism',
                'Jason Radcliff',
                'Thomas F. Torrance and the problem of universalism',
                'A peer-reviewed study arguing that Torrance allowed the possibility of universal salvation while refusing to make a final determination before Christ’s return.',
                'https://www.cambridge.org/core/journals/scottish-journal-of-theology/article/abs/thomas-f-torrance-and-the-problem-of-universalism/A745F9DBCD3DAF88070860C615680EDC',
            ),
            source(
                'catechism-universal',
                'Catechism of the Catholic Church',
                'Article 12, IV. Hell',
                'An official Catholic account affirming God’s desire for salvation while maintaining that definitive self-exclusion and eternal loss remain possible.',
                'https://www.vatican.va/content/catechism/en/part_one/section_two/chapter_three/article_12/iv_hell.html',
            ),
            source(
                'westminster-universal',
                'Orthodox Presbyterian Church',
                'Westminster Confession of Faith, Chapters 32–33',
                'A Reformed confessional account of final judgement in which the righteous enter everlasting life and the wicked everlasting punishment.',
                'https://www.opc.org/documents/MESV_col.html',
            ),
            source(
                'stanford-universalism',
                'Stanford Encyclopedia of Philosophy',
                'Heaven and Hell in Christian Thought',
                'An academic analysis of universalism, freedom, divine justice, and the traditional and annihilationist alternatives.',
                'https://plato.stanford.edu/entries/heaven-hell/',
            ),
        ],
        generation,
    },
    {
        slug: 'what-happens-after-death',
        category: 'Death and Final Things',
        question: 'What happens after death according to Christianity?',
        shortTitle: 'What happens after death?',
        summary: 'Historic Christianity centres its final hope on bodily resurrection, judgement, and renewed creation, but traditions describe the period between death and resurrection differently. Many teach a conscious intermediate state, Catholics also teach purification for some of the saved, while Seventh-day Adventists describe death as unconscious sleep until resurrection.',
        introduction: 'Questions about “going to heaven when we die” often combine two stages that the New Testament distinguishes: the condition of the dead before Christ’s return and the final embodied life that follows resurrection and judgement. The readings below compare accounts of the intermediate state and then recover the shared resurrection horizon. They do not determine which final view of hell is correct; that question is treated separately.',
        seoDescription: 'Compare Christian views of the afterlife: conscious presence, soul sleep, purgatory, bodily resurrection, judgement, and new creation.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Luke 23:32–43', href: '/read/Luke.23/#Luke.23.32-Luke.23.43' },
            { label: '1 Corinthians 15:35–58', href: '/read/1Cor.15/#1Cor.15.35-1Cor.15.58' },
            { label: 'Philippians 1:19–26', href: '/read/Phil.1/#Phil.1.19-Phil.1.26' },
            { label: 'Revelation 21:1–8', href: '/read/Rev.21/#Rev.21.1-Rev.21.8' },
        ],
        readings: [
            {
                id: 'conscious-intermediate-state',
                title: 'The dead remain conscious while awaiting resurrection',
                thesis: 'At death the person remains consciously before God in blessedness or judgement while awaiting bodily resurrection and the public final judgement.',
                accent: 'navy',
                badges: ['Traditional Protestant', 'Reformed', 'Evangelical', 'Conservative'],
                summary: 'This widespread Protestant account distinguishes the soul’s immediate state from the final resurrection. Those who die in Christ are consciously with him, yet their hope is incomplete until the body is raised. Those under judgement are likewise conscious while awaiting the final sentence. Death therefore does not erase personal awareness, but neither does the intermediate state replace resurrection.',
                contribution: 'It gives a direct account of passages that speak of departing to be with Christ, the thief entering paradise, and conscious figures awaiting judgement. It also preserves resurrection as a future completion rather than treating disembodied existence as the final Christian hope.',
                caution: 'The view must explain biblical language of death as sleep and avoid importing a body–soul dualism that makes bodily resurrection appear optional or merely decorative.',
                supporters: [
                    { name: 'Westminster Confession of Faith', sourceId: 'westminster-after-death' },
                ],
                predicates: [
                    {
                        label: 'Personal consciousness can continue under blessing or judgement after death',
                        topicSlug: 'what-is-eternal-conscious-torment',
                        readingId: 'retributive-eternal-punishment',
                        linked: true,
                    },
                    {
                        label: 'Final loss remains a possible outcome of resurrection and judgement',
                        topicSlug: 'what-is-christian-universalism',
                        readingId: 'final-loss-remains-possible',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'unconscious-sleep-until-resurrection', linked: true },
                    { type: 'overlaps with', target: 'purification-before-full-communion', linked: true },
                    { type: 'awaits', target: 'resurrection-and-new-creation', linked: true },
                ],
            },
            {
                id: 'unconscious-sleep-until-resurrection',
                title: 'The dead remain unconscious until resurrection',
                thesis: 'Death is an unconscious state without awareness of passing time until God raises the person for judgement and final destiny.',
                accent: 'amber',
                badges: ['Adventist', 'Conditionalist', 'Restorationist', 'Biblical Theology'],
                summary: 'This account takes Scripture’s sleep language as a description of the person’s actual condition, not only the appearance of the body. Body and life cease together at death, and the dead do not enjoy or suffer a separate conscious existence. Their next awareness is resurrection: the righteous to life and the wicked to judgement.',
                contribution: 'It tightly joins human identity to embodied life and makes resurrection indispensable. It also avoids speculation about disembodied consciousness and connects naturally with conditional immortality.',
                caution: 'The view must explain passages that appear to place the departed consciously with Christ and show why parables, visions, and promises of paradise should not describe an aware intermediate state.',
                supporters: [
                    { name: 'Seventh-day Adventist Church', sourceId: 'adventist-death-resurrection' },
                ],
                predicates: [
                    {
                        label: 'Immortality is a gift received at resurrection rather than an innate human possession',
                        topicSlug: 'does-final-punishment-end-in-annihilation',
                        readingId: 'conditional-immortality',
                        linked: true,
                    },
                    {
                        label: 'The wicked finally perish instead of remaining conscious for ever',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'terminal-destruction',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'conscious-intermediate-state', linked: true },
                    { type: 'contrasts with', target: 'purification-before-full-communion', linked: true },
                    { type: 'awaits', target: 'resurrection-and-new-creation', linked: true },
                ],
            },
            {
                id: 'purification-before-full-communion',
                title: 'Some receive judgement and purification after death',
                thesis: 'Each person receives judgement at death, and some who are saved undergo a final purification before entering the full joy of heaven.',
                accent: 'red',
                badges: ['Catholic', 'Sacramental', 'Traditional', 'Moderate'],
                summary: 'Catholic teaching distinguishes eternal destiny from the purification of those who die in God’s grace but are not yet fully sanctified. This purification is not a second opportunity to earn salvation and is categorically different from the punishment of the damned. The person’s ultimate destiny is determined in the particular judgement, while resurrection and the last judgement remain future.',
                contribution: 'It addresses the pastoral and theological question of how people genuinely united to Christ but still imperfectly healed are prepared for complete communion with a holy God. It also retains both immediate personal judgement and future bodily resurrection.',
                caution: 'The view must demonstrate its scriptural basis, distinguish purification clearly from further merit or a second probation, and explain why sanctification cannot be completed directly by God at death without a distinct purgatorial state.',
                supporters: [
                    { name: 'Catechism of the Catholic Church', sourceId: 'catechism-particular-judgement' },
                    { name: 'Catechism of the Catholic Church', sourceId: 'catechism-purgatory' },
                ],
                predicates: [
                    {
                        label: 'Christians may hope widely without denying the reality of particular judgement',
                        topicSlug: 'what-is-christian-universalism',
                        readingId: 'hopeful-universalism',
                        linked: true,
                    },
                    {
                        label: 'Final loss remains possible even though purification concerns only the saved',
                        topicSlug: 'what-is-christian-universalism',
                        readingId: 'final-loss-remains-possible',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'develops', target: 'conscious-intermediate-state', linked: true },
                    { type: 'contrasts with', target: 'unconscious-sleep-until-resurrection', linked: true },
                    { type: 'awaits', target: 'resurrection-and-new-creation', linked: true },
                ],
            },
            {
                id: 'resurrection-and-new-creation',
                title: 'God will raise bodies and renew creation',
                thesis: 'The final Christian hope is not permanent disembodied existence but the resurrection of the whole person into God’s judged, healed, and renewed creation.',
                accent: 'teal',
                badges: ['Ecumenical', 'Orthodox', 'Catholic', 'Protestant'],
                summary: 'Across their disagreements about the intermediate state, historic Christian traditions confess the resurrection of the dead and the life of the world to come. God does not abandon bodily creation; Christ raises the dead, judges evil, and brings creation to its intended fulfilment. “Heaven” is therefore incomplete if it means only souls elsewhere rather than embodied communion with God in renewed creation.',
                contribution: 'It places the emphasis where the creeds and 1 Corinthians 15 place it: God’s victory over death through bodily resurrection. This integrates personal hope with the healing of creation, justice, community, and the public reign of Christ.',
                caution: 'Shared resurrection language does not remove the disputes about the intermediate state or final destiny. The view must avoid using broad agreement about new creation to conceal real differences about judgement, hell, purification, and universal reconciliation.',
                supporters: [
                    { name: 'Orthodox Church in America', sourceId: 'oca-eternal-life' },
                    { name: 'Westminster Confession of Faith', sourceId: 'westminster-after-death' },
                    { name: 'Catechism of the Catholic Church', sourceId: 'catechism-particular-judgement' },
                ],
                predicates: [
                    {
                        label: 'The final goal is embodied participation in divine life and communion',
                        topicSlug: 'what-is-holiness',
                        readingId: 'participation-in-divine-life',
                        linked: true,
                    },
                    {
                        label: 'Christian universalism interprets renewed creation as the restoration of every person',
                        topicSlug: 'what-is-christian-universalism',
                        readingId: 'confident-universal-reconciliation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'completes', target: 'conscious-intermediate-state', linked: true },
                    { type: 'completes', target: 'unconscious-sleep-until-resurrection', linked: true },
                    { type: 'completes', target: 'purification-before-full-communion', linked: true },
                    {
                        type: 'raises',
                        label: 'Is hell eternal?',
                        topicSlug: 'is-hell-eternal',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'westminster-after-death',
                'Orthodox Presbyterian Church',
                'Westminster Confession of Faith, Chapters 32–33',
                'A Reformed confessional account of conscious existence after death, bodily resurrection, and the last judgement.',
                'https://www.opc.org/documents/MESV_col.html',
            ),
            source(
                'adventist-death-resurrection',
                'Seventh-day Adventist Church',
                'Death and Resurrection',
                'An official Adventist account of death as an unconscious state for all people until resurrection.',
                'https://adventist.org/beliefs/official/death-and-resurrection',
            ),
            source(
                'catechism-particular-judgement',
                'Catechism of the Catholic Church',
                'Article 12, I. The Particular Judgment',
                'An official Catholic account of immediate personal judgement at death, heaven with possible purification, or everlasting loss.',
                'https://www.vatican.va/content/catechism/en/part_one/section_two/chapter_three/article_12/i_the_particular_judgment.html',
            ),
            source(
                'catechism-purgatory',
                'Catechism of the Catholic Church',
                'Article 12, III. The Final Purification, or Purgatory',
                'An official Catholic account of the postmortem purification of people who die in God’s grace and are assured of salvation.',
                'https://www.vatican.va/content/catechism/en/part_one/section_two/chapter_three/article_12/iii_the_final_purification%2C_or_purgatory.html',
            ),
            source(
                'oca-eternal-life',
                'Orthodox Church in America',
                'Eternal Life',
                'An Orthodox account of bodily resurrection, renewed creation, final judgement, and eternal communion with God rather than a merely disembodied afterlife.',
                'https://www.oca.org/orthodoxy/the-orthodox-faith/doctrine-scripture/the-symbol-of-faith/eternal-life',
            ),
            source(
                'oca-final-judgement',
                'Orthodox Church in America',
                'The Final Judgment',
                'An Orthodox account of Christ’s universal final judgement and the moral seriousness of embodied love and service.',
                'https://www.oca.org/orthodoxy/the-orthodox-faith/spirituality/the-kingdom-of-heaven/the-final-judgment',
            ),
        ],
        generation,
    },
];
